import type { LeistungKalkulation } from "@/pages/dokumente/documentPositionModel";
import {
  calcLeistungLohnNetto,
  calcLeistungMaterialNetto,
} from "@/pages/dokumente/documentPositionModel";

export type LeistungVkDriver = "kalkuliert" | "pauschale" | "manuell";

function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Material + Lohn + Leistungs-Aufschlag – ohne Pauschale. Gesamtzeilen sind auf die Menge umgelegt. */
export function calcLeistungKalkuliertNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  if (!kalkulation) return 0;
  let subtotal = calcLeistungMaterialNetto(kalkulation, positionsMenge);
  subtotal += calcLeistungLohnNetto(kalkulation, positionsMenge);
  const aufschlag = Number(kalkulation.aufschlag_prozent || 0);
  if (aufschlag) subtotal *= 1 + aufschlag / 100;
  return round2(subtotal);
}

export function calcLeistungZwischensummeNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  if (!kalkulation) return 0;
  return round2(
    calcLeistungMaterialNetto(kalkulation, positionsMenge) +
      calcLeistungLohnNetto(kalkulation, positionsMenge),
  );
}

export function calcLeistungAufschlagBetragNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  positionsMenge: unknown = 1,
): number {
  const zwischensumme = calcLeistungZwischensummeNetto(kalkulation, positionsMenge);
  const aufschlag = Number(kalkulation?.aufschlag_prozent || 0);
  if (!aufschlag || zwischensumme <= 0) return 0;
  return round2(zwischensumme * (aufschlag / 100));
}

export function resolveLeistungVkDriver(
  kalkulation: LeistungKalkulation | null | undefined,
): LeistungVkDriver {
  const driver = kalkulation?.vk_driver;
  if (driver === "pauschale" || driver === "manuell" || driver === "kalkuliert") {
    return driver;
  }
  return "kalkuliert";
}

export function resolveLeistungVkNetto(
  kalkulation: LeistungKalkulation | null | undefined,
  fallbackEinzelpreis = 0,
  positionsMenge: unknown = 1,
): number {
  const kalkuliert = calcLeistungKalkuliertNetto(kalkulation, positionsMenge);
  const driver = resolveLeistungVkDriver(kalkulation);
  const pauschale = Number(kalkulation?.pauschale_netto || 0);
  if (driver === "pauschale" && pauschale > 0) return round2(pauschale);
  if (driver === "manuell" && Number.isFinite(fallbackEinzelpreis) && fallbackEinzelpreis > 0) {
    return round2(fallbackEinzelpreis);
  }
  if (kalkuliert > 0) return kalkuliert;
  if (Number.isFinite(fallbackEinzelpreis) && fallbackEinzelpreis > 0) {
    return round2(fallbackEinzelpreis);
  }
  return 0;
}

export function patchLeistungPauschale(
  kalkulation: LeistungKalkulation,
  pauschale: number,
): LeistungKalkulation {
  const value = round2(Math.max(0, pauschale));
  if (value <= 0) {
    return {
      ...kalkulation,
      pauschale_netto: 0,
      vk_driver: "kalkuliert",
    };
  }
  return {
    ...kalkulation,
    pauschale_netto: value,
    vk_driver: "pauschale",
  };
}

export function patchLeistungVkManuell(
  kalkulation: LeistungKalkulation,
  vk: number,
  positionsMenge: unknown = 1,
): LeistungKalkulation {
  const kalkuliert = calcLeistungKalkuliertNetto(kalkulation, positionsMenge);
  const value = round2(Math.max(0, vk));
  if (Math.abs(value - kalkuliert) < 0.005) {
    return {
      ...kalkulation,
      pauschale_netto: 0,
      vk_driver: "kalkuliert",
    };
  }
  return {
    ...kalkulation,
    pauschale_netto: value,
    vk_driver: "manuell",
  };
}

export function syncLeistungVkDriverOnKalkulationChange(
  kalkulation: LeistungKalkulation,
): LeistungKalkulation {
  if (resolveLeistungVkDriver(kalkulation) !== "kalkuliert") return kalkulation;
  return { ...kalkulation, pauschale_netto: 0 };
}
